import React, { KeyboardEvent, useCallback, useContext, useEffect, useState } from 'react'
import { LOGGER } from '@cicero/data/utils'
import { Popover, Switch, Table, TableBody, TableCell, TableContainer, TableRow, TextField, cn, makeStyles, useObservable } from '@cicero/ui'
import {
  AttributeDoc,
  AttributeSchema,
  AttributeType,
  AttributeValueDoc,
  AttributeValueSchema,
  IAttributeValueLookup,
  PageDoc,
  StoreContext,
  useModel,
} from 'models'
import { AttributeOptionChooser, AttributeOptionRenderer } from './AttributeOptionChooser'
import { FormulaRenderer } from './FormulaCell'
import { PageChooser, PageOptionRenderer } from './PageChooser'
import { UserChooser, UserListRenderer } from './UserChooser'

export const AttributeTable = ({ attributes, page, editable }: { attributes: AttributeDoc[], page: PageDoc|null, editable?: boolean }) => {
  const { labelCell, valueCell, valueEditable, editingPopover } = useStyles()
  const AttributeValues = useModel(AttributeValueSchema)
  const [valueLookup, setValueLookup] = useState<IAttributeValueLookup>({})
  const [anchorEl, setAnchor] = useState<HTMLElement|null>(null)
  const [stopSignal, setStopSignal] = useState(false)
  const [editingState, setEditingState] = useState<{ attribute: AttributeDoc, values: AttributeValueDoc[] }|null>(null)
  const editing = Boolean(anchorEl)

  // Get attribute values and index them by attribute
  useEffect(() => {
    if (page === null) { return }
    const perform = async () => {
      setValueLookup(AttributeValueSchema.UTILS.indexBy(
        await AttributeValues.QUERY({ selector: { page_id: page.id } }),
        'attribute_id'
      ))
    }
    perform()
  }, [page?.id])

  // Shows the popover to allow editing an attribute
  const enableEditing = useCallback((target: HTMLElement, attribute: AttributeDoc, values: AttributeValueDoc[]) => {
    if (!editable || !page) { return }
    setAnchor(target)
    setEditingState({ attribute, values })
  }, [editable, page])

  // Pass down a message to the editor to validate, save and stop editing
  const shouldStopEditing = useCallback((ev: KeyboardEvent) => {
    if (ev.key === 'Escape') {
      // ev.preventDefault()
    } else {
      setStopSignal(true)
    }
  }, [])

  // Called from below after changes have been saved, as a signal to hide the edit popover
  const afterEdit = useCallback((values?: AttributeValueDoc[]) => {
    if (values && editingState !== null) {
      const { attribute } = editingState
      if (attribute.id) {
        setValueLookup({ ...valueLookup, [attribute?.id]: values })
      }
    }

    setAnchor(null)
    setEditingState(null)
    setStopSignal(false)
  }, [editingState])

  return <TableContainer>
    <Table size="small">
      <TableBody>
        { attributes.map((attr) => {
          const values = attr.id ? valueLookup[attr.id] || [] : []
          return <TableRow key={attr.key}>
            <TableCell className={labelCell}><label>{attr.label}</label></TableCell>
            <TableCell onClick={(e) => enableEditing(e.currentTarget, attr, values)} className={cn(valueCell, { [valueEditable]: editable })}>
              <AttributeValue attribute={attr} values={values} />
            </TableCell>
          </TableRow>
        }) }
      </TableBody>
    </Table>

    <Popover
      open={editing}
      className={editingPopover}
      onClose={shouldStopEditing}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      { editingState && page
        ? <AttributeValueEditor
          attribute={editingState.attribute}
          values={editingState.values}
          page={page}
          shouldSubmit={stopSignal}
          onSubmit={(values: AttributeValueDoc[]) => afterEdit(values)}
          onCancel={() => afterEdit()}
        /> : null
      }
    </Popover>
  </TableContainer>
}

export const AttributeRenderer = ({ attribute, page, compact }: { attribute: AttributeDoc, page: PageDoc, compact?: boolean }) => {
  const { valueWrapper } = useStyles()
  const AttributeValues = useModel(AttributeValueSchema)
  const values = useObservable(() => AttributeValues.QUERY$(AttributeValueSchema.QUERIES.attrs(attribute.id, [page.id])), [], [page, attribute])

  return <span className={cn(valueWrapper)}>
    <AttributeValue attribute={attribute} values={values} compact={compact} />
  </span>
}

export const AttributeValue = ({ attribute, values, compact }: { attribute: AttributeDoc, values: AttributeValueDoc[], compact?: boolean }) => {
  if (values.length === 0) { return null }
  switch (attribute.type) {
    case AttributeType.formula:
      const value = values.length ? AttributeValueSchema.getValueString(values[0]) : ''
      return <FormulaRenderer value={value} />
    case AttributeType.option:
      return <AttributeOptionRenderer attribute={attribute} values={values} />
    case AttributeType.linkedpage:
      return <PageOptionRenderer attribute={attribute} values={values} />
    case AttributeType.linkeduser:
      return <UserListRenderer ids={values.map(v => v.linked_user_id as string)} size={compact ? 32 : undefined} />
    default:
      const config = AttributeSchema.TYPE_CONFIG[attribute.type]
      let content = values.length ? values[0][config.valueKey] : ''
      if (typeof content !== 'string') { content = String(content) }
      if (values.length > 1) { LOGGER.warn('A basic attribute has more than 1 value', attribute.label, values) }
      return <>{content}</>
  }
}

export const AttributeValueEditor = ({
  attribute,
  values,
  page,
  onSubmit,
  onCancel,
  shouldSubmit,
}: {
  attribute: AttributeDoc
  values: AttributeValueDoc[]
  page: PageDoc
  onSubmit: (values: AttributeValueDoc[]) => void
  onCancel: () => void
  shouldSubmit: boolean
}) => {
  const [value, setValue] = useState<any>(null)
  const store = useContext(StoreContext)

  useEffect(() => {
    if (!attribute) { return }
    const typeConfig = AttributeSchema.TYPE_CONFIG[attribute.type]
    if (typeConfig?.prepFormValue) {
      setValue(typeConfig.prepFormValue(attribute, values))
    }
  }, [attribute?.id])

  useEffect(() => {
    if (shouldSubmit) { saveChanges() }
  }, [shouldSubmit])

  const handleKeyPress = (ev: KeyboardEvent) => {
    switch (ev.key) {
      case 'Enter': return saveChanges()
      case 'Escape': return onCancel()
    }
  }

  const saveChanges = useCallback(() => {
    if (!attribute) { return onCancel() }
    const persistChanges = async () => {
      const typeConfig = AttributeSchema.TYPE_CONFIG[attribute.type]
      const changes = typeConfig?.prepValue ? typeConfig.prepValue(value) : value
      const latestValues = await AttributeSchema.HOOKS.persistAttributeValueChanges(store, page, attribute, values, changes)
      onSubmit(latestValues)
    }
    persistChanges()
  }, [attribute.id, values, value])

  switch (attribute.type) {
    case AttributeType.option:
      return <AttributeOptionChooser
        autoFocus={true}
        alwaysOpen={true}
        name={attribute.key}
        selectedValues={values}
        attribute={attribute}
        value={value || []}
        onChange={(e) => { setValue(e.target.value) }}
        onBlur={() => saveChanges()}
      />
    case AttributeType.linkedpage:
      return <PageChooser
        autoFocus={true}
        alwaysOpen={true}
        name={attribute.key}
        selectedValues={values}
        value={value || []}
        onChange={(e) => { setValue(e.target.value) }}
        onBlur={() => saveChanges()}
      />
    case AttributeType.linkeduser:
      return <UserChooser
        autoFocus={true}
        alwaysOpen={true}
        name={attribute.key}
        selectedValues={values}
        value={value || []}
        onChange={(e) => { setValue(e.target.value) }}
        onBlur={() => saveChanges()}
      />
    case AttributeType.date:
      return <TextField value={value} type="datetime-local" onChange={(e) => setValue(e.target.value)} autoFocus onKeyUp={handleKeyPress} />
    case AttributeType.number:
      return <TextField value={value} type="number" onChange={(e) => setValue(e.target.value)} autoFocus onKeyUp={handleKeyPress} />
    case AttributeType.boolean:
      return <Switch checked={value} onChange={(e) => setValue(e.target.checked)} />
    default:
      return <TextField value={value} onChange={(e) => setValue(e.target.value)} autoFocus onKeyUp={handleKeyPress} />
  }
}

const useStyles = makeStyles({
  labelCell: {
    fontWeight: 'bold',
  },
  valueWrapper: {
    margin: '6px 0',
    display: 'block',
  },
  valueWrapperInline: {
    display: 'flex',
  },
  valueCell: {
    textAlign: 'right',
  },
  valueEditable: {
    cursor: 'pointer',
    '&:hover': {
      background: '#EEE',
    },
  },
  editingPopover: {
    minWidth: 300,
  },
  editingElement: {
    minHeight: 400,
  },
})
